import { useEffect, useState } from "react";
import {
  Loader2, Search, Mail, RefreshCw, ChevronRight, X,
  CheckCircle2, Clock, AlertCircle, Trash2, Inbox
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  pending_review: { label: "Pending Review", color: "amber", icon: Clock },
  in_review:      { label: "In Review",      color: "blue",  icon: Clock },
  interview:      { label: "Interview",      color: "violet",icon: ChevronRight },
  accepted:       { label: "Accepted",       color: "emerald",icon: CheckCircle2 },
  rejected:       { label: "Rejected",       color: "red",   icon: X },
  waitlist:       { label: "Waitlist",       color: "slate", icon: AlertCircle },
  withdrawn:      { label: "Withdrawn",      color: "slate", icon: X },
};
const STATUS_ORDER = ["pending_review","in_review","interview","accepted","waitlist","rejected","withdrawn"];

const colorClasses = {
  amber:   "bg-amber-500/10 border-amber-500/30 text-amber-300",
  blue:    "bg-blue-500/10 border-blue-500/30 text-blue-300",
  violet:  "bg-violet-500/10 border-violet-500/30 text-violet-300",
  emerald: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300",
  red:     "bg-red-500/10 border-red-500/30 text-red-300",
  slate:   "bg-slate-500/10 border-slate-500/30 text-slate-300",
};

const authHeaders = () => {
  const t = localStorage.getItem("auth_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function ApplicationsInbox() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [savingStatus, setSavingStatus] = useState(false);
  const [note, setNote] = useState("");

  const fetchInbox = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter) params.set("status", filter);
      if (search.trim()) params.set("search", search.trim());
      const res = await fetch(`${API_URL}/api/admin/applications?${params}`, { headers: authHeaders() });
      if (res.status === 401) { setItems([]); setLoading(false); return; }
      const data = await res.json();
      setItems(data.items || []);
      setCounts(data.status_counts || {});
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInbox(); /* eslint-disable-next-line */ }, [filter]);

  const updateStatus = async (id, status) => {
    setSavingStatus(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/applications/${id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ status, note: note || undefined }),
      });
      if (res.ok) {
        const data = await res.json();
        setSelected(data.application);
        setNote("");
        fetchInbox();
      }
    } finally {
      setSavingStatus(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Permanently delete this application?")) return;
    const res = await fetch(`${API_URL}/api/admin/applications/${id}`, { method: "DELETE", headers: authHeaders() });
    if (res.ok) { setSelected(null); fetchInbox(); }
  };

  const total = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-5" data-testid="admin-applications-inbox">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Inbox className="w-6 h-6 text-purple-400" />
              Applications Inbox
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              {total} total · {counts.pending_review || 0} pending review
            </p>
          </div>
          <Button onClick={fetchInbox} variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-700">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {/* Status pills */}
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setFilter("")}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors
              ${!filter ? "bg-purple-500/20 border-purple-500/40 text-purple-200" : "bg-slate-800 border-slate-700 text-slate-400 hover:text-white"}`}>
            All ({total})
          </button>
          {STATUS_ORDER.filter((s) => counts[s]).map((s) => {
            const meta = STATUS_META[s];
            return (
              <button key={s} onClick={() => setFilter(s)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors
                  ${filter === s ? colorClasses[meta.color] : "bg-slate-800 border-slate-700 text-slate-400 hover:text-white"}`}>
                {meta.label} ({counts[s]})
              </button>
            );
          })}
        </div>

        <div className="flex gap-2">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchInbox()}
              placeholder="Search name, email, program…"
              className="pl-9 bg-slate-800 border-slate-700 text-white"
              data-testid="applications-search"
            />
          </div>
          <Button onClick={fetchInbox} className="bg-purple-600 hover:bg-purple-700 text-white">Search</Button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-7 h-7 animate-spin text-purple-400" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 bg-slate-800/50 border border-slate-700 rounded-xl">
          <Inbox className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">No applications match this filter.</p>
        </div>
      ) : (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden divide-y divide-slate-700">
          {items.map((app) => {
            const meta = STATUS_META[app.status] || STATUS_META.pending_review;
            return (
              <button
                key={app.id}
                onClick={() => { setSelected(app); setNote(""); }}
                className="w-full grid grid-cols-12 gap-3 px-4 py-4 text-left hover:bg-slate-700/40 transition-colors"
                data-testid={`application-row-${app.id}`}
              >
                <div className="col-span-12 sm:col-span-4 min-w-0">
                  <p className="text-white font-medium truncate">{app.name}</p>
                  <p className="text-xs text-slate-400 truncate">{app.email}</p>
                </div>
                <div className="col-span-7 sm:col-span-4 min-w-0 text-sm text-slate-300 truncate">
                  {app.program}
                </div>
                <div className="col-span-5 sm:col-span-2 flex items-center justify-end sm:justify-start">
                  <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-medium border ${colorClasses[meta.color]}`}>
                    {meta.label}
                  </span>
                </div>
                <div className="col-span-12 sm:col-span-2 text-xs text-slate-500 text-right">
                  {new Date(app.submittedAt || Date.now()).toLocaleDateString()}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex" data-testid="application-detail">
          <button className="flex-1 bg-black/60" onClick={() => setSelected(null)} aria-label="Close" />
          <aside className="w-full max-w-xl bg-slate-900 border-l border-slate-700 h-full overflow-y-auto">
            <header className="sticky top-0 z-10 bg-slate-900/95 backdrop-blur p-5 border-b border-slate-700 flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-sm text-slate-400">Application</p>
                <p className="text-white font-semibold truncate">{selected.name}</p>
              </div>
              <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white" onClick={() => setSelected(null)}>
                <X className="w-4 h-4" />
              </Button>
            </header>
            <div className="p-5 space-y-5 text-sm">
              <FieldRow label="Email">
                <a href={`mailto:${selected.email}`} className="text-blue-400 hover:underline inline-flex items-center gap-1">
                  <Mail className="w-3.5 h-3.5" /> {selected.email}
                </a>
              </FieldRow>
              <FieldRow label="Phone">{selected.phone || "—"}</FieldRow>
              <FieldRow label="Program">{selected.program}</FieldRow>
              <FieldRow label="Submitted">{new Date(selected.submittedAt || Date.now()).toLocaleString()}</FieldRow>
              <FieldRow label="Reference"><span className="font-mono text-xs text-slate-400">{selected.id}</span></FieldRow>
              <Section label="Background">{selected.background || "—"}</Section>
              <Section label="Motivation">{selected.motivation || "—"}</Section>

              {(selected.notes || []).length > 0 && (
                <Section label="Review Notes">
                  <ul className="space-y-2">
                    {selected.notes.map((n, i) => (
                      <li key={i} className="text-xs text-slate-300 bg-slate-800/50 rounded p-2 border border-slate-700">
                        <span className="text-slate-500">{new Date(n.at).toLocaleString()} · {n.by} → <strong>{STATUS_META[n.status]?.label || n.status}</strong></span>
                        <div className="mt-1 whitespace-pre-wrap">{n.text}</div>
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              <div className="pt-3 border-t border-slate-700 space-y-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Update status</label>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Optional review note…"
                    rows={2}
                    className="w-full mt-2 px-3 py-2 rounded bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/40"
                    data-testid="status-note"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  {STATUS_ORDER.map((s) => {
                    const meta = STATUS_META[s];
                    const active = selected.status === s;
                    return (
                      <button
                        key={s}
                        disabled={savingStatus || active}
                        onClick={() => updateStatus(selected.id, s)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors
                          ${active ? colorClasses[meta.color] + " opacity-70 cursor-default"
                            : "bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700"}`}
                        data-testid={`set-status-${s}`}
                      >
                        {meta.label}
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center justify-end gap-2 pt-3">
                  <Button variant="ghost" size="sm" className="text-red-400 hover:bg-red-500/10" onClick={() => remove(selected.id)}>
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <div className="grid grid-cols-3 gap-3">
      <div className="text-slate-500 uppercase text-[10px] tracking-widest font-semibold">{label}</div>
      <div className="col-span-2 text-slate-100">{children}</div>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <div className="text-slate-500 uppercase text-[10px] tracking-widest font-semibold mb-1.5">{label}</div>
      <div className="text-slate-100 whitespace-pre-wrap leading-relaxed">{children}</div>
    </div>
  );
}
